import React from "react";
import {
  Plus, Users, Image as ImageIcon, Clapperboard, UploadCloud,
  BookOpen, Film, Download, Layers, Sparkles, ArrowRight, Check
} from "lucide-react";
import { Section, DemoButton, DemoCard, DemoTag, TipBox, DemoInput, DemoImageArea, StepIndicator } from "./HelpComponents";

const HelpQuickStart = ({ theme, expandedSection, toggleSection }) => {
  const sections = [
    { id: 0, title: "新建项目", icon: Plus },
    { id: 1, title: "拆分剧情大纲", icon: BookOpen },
    { id: 2, title: "新建集", icon: Layers },
    { id: 3, title: "导入剧本", icon: UploadCloud },
    { id: 4, title: "小说直转", icon: Sparkles },
    { id: 5, title: "生成分镜", icon: Clapperboard },
    { id: 6, title: "一键生图", icon: ImageIcon },
    { id: 7, title: "一键生视频", icon: Film },
    { id: 8, title: "下载视频", icon: Download },
  ];

  return (
    <>
      {/* 流程概览 */}
      <div className="flex items-center gap-2 overflow-x-auto pb-3 mb-4">
        {sections.map((s, idx) => (
          <React.Fragment key={s.id}>
            <button onClick={() => toggleSection(s.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border shrink-0 transition-colors ${expandedSection === s.id ? '' : 'bg-[var(--color-bg-secondary)] hover:bg-[var(--color-bg-tertiary)]'}`}
              style={{
                backgroundColor: expandedSection === s.id ? `${theme.colors.accentPrimary}20` : undefined,
                borderColor: expandedSection === s.id ? theme.colors.accentPrimary : "var(--color-border-primary)",
                color: expandedSection === s.id ? theme.colors.accentPrimary : "var(--color-text-secondary)"
              }}>
              <s.icon size={14} />
              <span className="text-xs font-medium whitespace-nowrap">{s.title}</span>
            </button>
            {idx < sections.length - 1 && <ArrowRight size={14} style={{ color: "var(--color-text-muted)" }} className="shrink-0" />}
          </React.Fragment>
        ))}
      </div>

      <div className="space-y-4">
        {/* 第一步 */}
        <Section id={0} title="第一步：新建项目" icon={Plus} expanded={expandedSection === 0} onToggle={toggleSection} theme={theme}>
          <div className="space-y-4">
            <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>进入平台首页，点击右上角的新建项目按钮：</p>
            <div className="flex items-center gap-3">
              <DemoButton icon={Plus} label="新建项目" theme={theme} size="sm" />
              <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>← 点击此按钮</span>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>在弹出的对话框中填写项目信息：</p>
            <div className="p-4 rounded-lg border space-y-3" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
              <DemoInput label="项目名称" placeholder="例如：修仙传" />
              <DemoInput label="简介（可选）" placeholder="作品类型、风格定位等..." />
            </div>
            <TipBox theme={theme}>一个项目建议对应一部完整作品，创建后可在首页项目列表中查看和管理。</TipBox>
          </div>
        </Section>

        {/* 第二步 */}
        <Section id={1} title="第二步：拆分剧情大纲" icon={BookOpen} expanded={expandedSection === 1} onToggle={toggleSection} theme={theme}>
          <div className="space-y-4">
            <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>进入项目后，在侧边栏找到"剧情大纲"入口：</p>
            <div className="flex items-center gap-2 p-3 rounded-lg border" style={{ backgroundColor: "var(--color-bg-tertiary)", borderColor: theme.colors.accentPrimary }}>
              <BookOpen size={18} style={{ color: theme.colors.accentPrimary }} />
              <span className="text-sm font-medium" style={{ color: theme.colors.accentPrimary }}>剧情大纲</span>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>在大纲编辑器中规划整体故事结构，将剧情拆分为多个章节/集数节点。</p>
            <TipBox theme={theme} type="warning">剧情大纲用于统筹管理长篇作品的整体结构，便于团队协作时统一故事方向。</TipBox>
          </div>
        </Section>

        {/* 第三步 */}
        <Section id={2} title="第三步：新建集" icon={Layers} expanded={expandedSection === 2} onToggle={toggleSection} theme={theme}>
          <div className="space-y-4">
            <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>在项目详情页，点击新建集数按钮：</p>
            <DemoButton icon={Plus} label="新建集数" theme={theme} size="sm" />
            <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>填写集数标题（如"第1集：初入仙门"），系统会自动分配排序序号。创建完成后点击进入该集的工作台。</p>
          </div>
        </Section>

        {/* 第四步 */}
        <Section id={3} title="第四步：导入剧本" icon={UploadCloud} expanded={expandedSection === 3} onToggle={toggleSection} theme={theme}>
          <div className="space-y-4">
            <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>进入集数工作台后，在侧边栏找到"导入剧本"：</p>
            <div className="flex items-center gap-2 p-3 rounded-lg border" style={{ backgroundColor: "var(--color-bg-tertiary)", borderColor: "var(--color-border-primary)" }}>
              <UploadCloud size={18} style={{ color: "var(--color-text-primary)" }} />
              <span className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>导入剧本</span>
            </div>
            <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>在文本编辑区粘贴剧本内容。建议按照以下格式编写：</p>
            <div className="p-4 rounded-lg font-mono text-xs leading-relaxed" style={{ backgroundColor: "var(--color-bg-overlay)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-secondary)" }}>
              <div style={{ color: theme.colors.accentPrimary }}>场景：青云山山门</div>
              <div style={{ color: theme.colors.info }}>人物：林小凡（18岁，青衣少年）、长老（白发老者）</div>
              <div className="mt-2">林小凡：弟子林小凡，求见掌门！</div>
              <div>长老：<span style={{ color: "var(--color-text-muted)" }}>（抚须）</span>你资质平庸，为何执着于修仙？</div>
            </div>
          </div>
        </Section>

        {/* 第五步 */}
        <Section id={4} title="第五步：小说直转" icon={Sparkles} expanded={expandedSection === 4} onToggle={toggleSection} theme={theme}>
          <div className="space-y-4">
            <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>导入剧本后，点击"开始生成"按钮，在弹窗中选择"小说直转"模式：</p>
            <DemoButton icon={Sparkles} label="小说直转" theme={theme} />
            <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>系统将执行两阶段处理：</p>
            <div className="grid grid-cols-2 gap-3">
              <DemoCard icon={Users} title="阶段一：资产提取" description="AI自动分析剧本，提取角色、场景、道具信息" theme={theme} />
              <DemoCard icon={Clapperboard} title="阶段二：分镜生成" description="基于确认的资产信息生成分镜表" theme={theme} />
            </div>
            <TipBox theme={theme} type="success">优势：自动保持角色形象一致性，智能识别场景和道具需求，支持使用已有资产。</TipBox>
          </div>
        </Section>

        {/* 第六步 */}
        <Section id={5} title="第六步：生成分镜" icon={Clapperboard} expanded={expandedSection === 5} onToggle={toggleSection} theme={theme}>
          <div className="space-y-4">
            <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>"小说直转"完成后，系统自动生成完整的分镜表。详细的分镜编辑器功能请查看"分镜编辑器"标签页。</p>
          </div>
        </Section>

        {/* 第七步 */}
        <Section id={6} title="第七步：一键生图" icon={ImageIcon} expanded={expandedSection === 6} onToggle={toggleSection} theme={theme}>
          <div className="space-y-4">
            <p className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>7.1 生图模型选择</p>
            <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>在界面左侧工具栏找到"模型选择器"，点击展开选择AI生图模型：</p>
            <div className="p-3 rounded-lg border" style={{ backgroundColor: "var(--color-bg-tertiary)", borderColor: "var(--color-border-primary)" }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-1.5 rounded-md" style={{ backgroundColor: "var(--color-bg-primary)", border: "1px solid var(--color-border-primary)" }}>
                    <Sparkles size={16} style={{ color: theme.colors.accentPrimary }} />
                  </div>
                  <div>
                    <div className="text-xs font-bold" style={{ color: "var(--color-text-primary)" }}>Gemini_线路4 / ChatGPT_线路4</div>
                    <div className="text-2xs" style={{ color: "var(--color-text-muted)" }}>推荐多模态与图文生成备用线路</div>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-sm font-bold mt-4" style={{ color: "var(--color-text-primary)" }}>7.2 画风设置</p>
            <div className="flex flex-wrap gap-2">
              <DemoTag label="古风国漫" theme={theme} />
              <DemoTag label="日系二次元" theme={theme} />
              <DemoTag label="电影写实" theme={theme} />
              <DemoTag label="赛博朋克" theme={theme} />
            </div>
            <p className="text-sm font-bold mt-4" style={{ color: "var(--color-text-primary)" }}>7.3 执行一键生图</p>
            <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>在分镜编辑器中选择需要生图的分镜，点击工具栏按钮：</p>
            <DemoButton icon={ImageIcon} label="一键生图" theme={theme} />
          </div>
        </Section>

        {/* 第八步 */}
        <Section id={7} title="第八步：一键生视频" icon={Film} expanded={expandedSection === 7} onToggle={toggleSection} theme={theme}>
          <div className="space-y-4">
            <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>图片生成完成后，选择已完成生图的分镜，点击工具栏按钮：</p>
            <DemoButton icon={Film} label="一键生视频" theme={theme} variant="purple" />
            <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>在弹窗中确认视频模型和比例设置，点击"开始生成"。</p>
            <TipBox theme={theme}>视频比例需与生图比例保持一致（如9:16或16:9），以获得最佳效果。</TipBox>
          </div>
        </Section>

        {/* 第九步 */}
        <Section id={8} title="第九步：下载视频" icon={Download} expanded={expandedSection === 8} onToggle={toggleSection} theme={theme}>
          <div className="space-y-4">
            <p className="text-sm leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>所有视频片段生成完成后，可以选择以下方式导出：</p>
            <div className="space-y-3">
              {[
                { icon: Download, title: "单镜下载", desc: '在分镜详情中点击"下载"按钮保存单个视频片段' },
                { icon: Download, title: "批量下载", desc: '进入"资源中心"，勾选多个视频后点击"批量下载"' },
                { icon: Film, title: "成片导出", desc: '点击"导出成片"按钮，系统自动拼接所有视频片段为完整MP4文件' },
              ].map((item, i) => (
                <div key={i} className="p-3 rounded-lg border" style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-primary)" }}>
                  <div className="flex items-center gap-2 mb-1">
                    <item.icon size={14} style={{ color: theme.colors.accentPrimary }} />
                    <span className="text-sm font-bold" style={{ color: "var(--color-text-primary)" }}>{item.title}</span>
                  </div>
                  <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* 底部 */}
        <div className="mt-6 p-4 rounded-xl" style={{ backgroundColor: `${theme.colors.success}15`, border: `1px solid ${theme.colors.success}40` }}>
          <div className="flex items-start gap-3">
            <Check size={18} style={{ color: theme.colors.success }} className="shrink-0 mt-0.5" />
            <p className="text-xs" style={{ color: theme.colors.success }}>建议先创建一个测试项目，用一小段剧本完整走一遍流程熟悉操作。</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default HelpQuickStart;
